/**
 * Subtareas en la ficha (ADR-0018): un solo nivel, madre → subtareas.
 *
 * - `BloqueMadre`: «Tarea madre» de esta tarea, con enlace, lápiz para elegirla o cambiarla y botón para
 *   quitarla. `padreId` es parte del requerimiento de la subtarea: solo quien la creó y ADMIN (la web oculta el
 *   lápiz con `puedeEditarRequerimiento`; la API decide). No se muestra si la tarea ya tiene subtareas.
 * - `SeccionSubtareas`: avance, lista y «Nueva subtarea» / «Vincular existente». No se muestra si la tarea es
 *   subtarea. Quitar una subtarea cambia el requerimiento DE ESA HIJA, así que el botón aparece solo en las
 *   hijas que quien mira creó (o para ADMIN); vincular una existente, igual.
 * - `BarraProgreso`: avance «hechas / total» con `<progress>` (sin estilos en línea: el CSP no los permite).
 *
 * Foco: al cerrar el buscador (Esc, X o al elegir) vuelve al botón que lo abrió, y al quitar una fila va al
 * siguiente botón de quitar (o al título de la sección). Si no, caería en `body` y Tab escaparía de la ficha.
 */
import { useEffect, useRef, useState } from 'react';
import {
  formatearNumero,
  puedeEditarRequerimiento,
  type ProgresoSubtareas,
  type Rol,
  type Tarea,
  type TareaDetalle,
} from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { BuscadorTarea } from './BuscadorTarea';
import { useCambiarMadre, useSubtareas } from './hooks';
import { clicEnlaceTarea, ListaRelacionadas } from './ListaRelacionadas';
import './Subtareas.css';

/** Quien mira la ficha (el rol exacto solo importa para ADMIN). */
export interface QuienMira {
  id: number;
  rol: Rol;
}

/** Barra fina de avance. Decorativa: el texto de al lado dice lo mismo. */
export function BarraProgreso({ progreso }: { progreso: ProgresoSubtareas }) {
  return (
    <progress
      className="barra-progreso"
      max={Math.max(progreso.total, 1)}
      value={progreso.hechas}
      aria-hidden="true"
    />
  );
}

/** La tarea de la ficha: con sus subtareas (las trae el detalle). */
export type TareaConSubtareas = Tarea & Pick<TareaDetalle, 'subtareas'>;

const yaEsSubtareaDe = (tarea: Tarea) =>
  tarea.padre ? `Ya es subtarea de ${formatearNumero(tarea.padre.numero)}` : null;

interface PropsMadre {
  tarea: TareaConSubtareas;
  /** Puede cambiar el requerimiento de ESTA tarea (creadora o ADMIN, y con escritura). */
  puedeEditar: boolean;
  onAbrir?: (id: number) => void;
}

export function BloqueMadre({ tarea, puedeEditar, onAbrir }: PropsMadre) {
  const [buscando, setBuscando] = useState(false);
  const cambiar = useCambiarMadre();
  const madre = tarea.padre;
  const lapiz = useRef<HTMLButtonElement>(null);
  // Pedido de devolver el foco al lápiz: se cumple cuando el buscador ya se cerró y nada se está guardando
  // (mientras guarda, el lápiz está deshabilitado y no se puede enfocar).
  const volverAlLapiz = useRef(false);
  useEffect(() => {
    if (!volverAlLapiz.current || buscando || cambiar.isPending || !lapiz.current) return;
    volverAlLapiz.current = false;
    lapiz.current.focus();
  });

  // Una madre no puede ser subtarea (un solo nivel); sin madre ni permiso no hay nada que mostrar.
  if (tarea.subtareas.length > 0 || (!madre && !puedeEditar)) return null;

  const hija = { id: tarea.id, numero: tarea.numero };
  const cerrar = () => {
    volverAlLapiz.current = true;
    setBuscando(false);
  };

  return (
    <div className="madre">
      <div className="madre__fila">
        <Icono nombre="rama" />
        <span className="madre__etiqueta">Tarea madre</span>
        {madre ? (
          <>
            {onAbrir ? (
              <a
                className="madre__id"
                href={`?tarea=${madre.id}`}
                onClick={(evento) => clicEnlaceTarea(evento, madre.id, onAbrir)}
              >
                {formatearNumero(madre.numero)}
              </a>
            ) : (
              <span className="madre__id">{formatearNumero(madre.numero)}</span>
            )}
            <span className="madre__titulo" title={madre.titulo}>
              {madre.titulo}
            </span>
          </>
        ) : (
          <span className="madre__vacio">Sin tarea madre</span>
        )}
        {puedeEditar && (
          <span className="madre__acciones">
            <button
              ref={lapiz}
              type="button"
              className="madre__boton"
              aria-label={madre ? 'Cambiar la tarea madre' : 'Elegir la tarea madre'}
              title={madre ? 'Cambiar la tarea madre' : 'Elegir la tarea madre'}
              aria-expanded={buscando}
              disabled={cambiar.isPending}
              onClick={() => setBuscando((actual) => !actual)}
            >
              <Icono nombre="lapiz" />
            </button>
            {madre && (
              <button
                type="button"
                className="madre__boton madre__boton--quitar"
                aria-label="Quitar la tarea madre"
                title="Quitar la tarea madre"
                disabled={cambiar.isPending}
                onClick={() => {
                  // El botón desaparece al quitarla: el foco va al lápiz (también si falla).
                  volverAlLapiz.current = true;
                  cambiar.mutate({ hija, madre: null, anterior: madre }, { onSuccess: cerrar });
                }}
              >
                <Icono nombre="x" />
              </button>
            )}
          </span>
        )}
      </div>
      {buscando && (
        <BuscadorTarea
          etiqueta="Buscar la tarea madre"
          tareaActualId={tarea.id}
          ocupado={cambiar.isPending}
          motivoDeshabilitada={(candidata) =>
            candidata.id === madre?.id ? 'Ya es su madre' : yaEsSubtareaDe(candidata)
          }
          onElegir={(candidata) =>
            cambiar.mutate(
              {
                hija,
                madre: { id: candidata.id, numero: candidata.numero },
                anterior: madre,
              },
              { onSuccess: cerrar },
            )
          }
          onCerrar={cerrar}
        />
      )}
    </div>
  );
}

interface PropsSeccion {
  tarea: TareaConSubtareas;
  quienMira: QuienMira;
  puedeEscribir: boolean;
  onAbrir?: (id: number) => void;
  /** Abre «Nueva tarea» con esta como madre (`?tarea=nueva&padre=N`). */
  onNuevaSubtarea?: (madreId: number) => void;
}

export function SeccionSubtareas({
  tarea,
  quienMira,
  puedeEscribir,
  onAbrir,
  onNuevaSubtarea,
}: PropsSeccion) {
  const [buscando, setBuscando] = useState(false);
  const cambiar = useCambiarMadre();
  const seccion = useRef<HTMLElement>(null);
  const titulo = useRef<HTMLHeadingElement>(null);
  const vincular = useRef<HTMLButtonElement>(null);
  // Adónde va el foco cuando termine lo que se está haciendo (ver el comentario del módulo).
  const foco = useRef<
    { a: 'vincular' } | { a: 'quitar'; hijaId: number; etiqueta: string; indice: number } | null
  >(null);
  useEffect(() => {
    const pedido = foco.current;
    if (!pedido || cambiar.isPending) return;
    if (pedido.a === 'vincular') {
      if (buscando || !vincular.current) return;
      foco.current = null;
      vincular.current.focus();
      return;
    }
    const botones = Array.from(
      seccion.current?.querySelectorAll<HTMLButtonElement>('.relacionadas__quitar') ?? [],
    );
    if (cambiar.isError) {
      // Falló: la fila sigue; el foco vuelve a su botón.
      foco.current = null;
      botones.find((b) => b.getAttribute('aria-label') === pedido.etiqueta)?.focus();
      return;
    }
    // Hasta que el detalle se refresque, la fila quitada sigue en pantalla.
    if (tarea.subtareas.some((h) => h.id === pedido.hijaId)) return;
    foco.current = null;
    (botones[Math.min(pedido.indice, botones.length - 1)] ?? titulo.current)?.focus();
  });
  const cerrarBuscador = () => {
    foco.current = { a: 'vincular' };
    setBuscando(false);
  };
  // Las hijas completas (con su creador) para saber de cuáles se puede quitar la madre. Solo si hay qué quitar.
  const completas = useSubtareas(
    puedeEscribir && tarea.subtareas.length > 0 && quienMira.rol !== 'ADMIN' ? tarea.id : null,
  );

  // Un solo nivel: una subtarea no tiene subtareas.
  if (tarea.padre) return null;

  const madre = { id: tarea.id, numero: tarea.numero };
  const progreso = tarea.progresoSubtareas;
  const puedeMoverHija = (hija: Pick<Tarea, 'creadaPor'>) =>
    puedeEscribir && puedeEditarRequerimiento(quienMira, hija);
  const etiquetaQuitar = (hijaId: number, numero: number) => {
    if (!puedeEscribir) return null;
    const completa =
      quienMira.rol === 'ADMIN'
        ? { creadaPor: null }
        : completas.data?.find((h) => h.id === hijaId);
    if (!completa || !puedeMoverHija(completa)) return null;
    return `Quitar ${formatearNumero(numero)} de las subtareas`;
  };

  return (
    <section ref={seccion} className="subtareas" aria-labelledby={`subtareas-titulo-${tarea.id}`}>
      <div className="subtareas__cab">
        <h3
          ref={titulo}
          id={`subtareas-titulo-${tarea.id}`}
          className="seccion-titulo"
          tabIndex={-1}
        >
          Subtareas
        </h3>
        {progreso && progreso.total > 0 && (
          <span className="subtareas__progreso">
            <BarraProgreso progreso={progreso} />
            <span>
              {progreso.hechas}/{progreso.total} hechas
            </span>
          </span>
        )}
      </div>
      {tarea.subtareas.length > 0 ? (
        <ListaRelacionadas
          tareas={tarea.subtareas}
          etiqueta={`Subtareas de ${formatearNumero(tarea.numero)}`}
          onAbrir={onAbrir}
          quitar={(hija) => etiquetaQuitar(hija.id, hija.numero)}
          onQuitar={(hija) => {
            const etiqueta = etiquetaQuitar(hija.id, hija.numero) ?? '';
            const indice = Array.from(
              seccion.current?.querySelectorAll('.relacionadas__quitar') ?? [],
            ).findIndex((b) => b.getAttribute('aria-label') === etiqueta);
            foco.current = { a: 'quitar', hijaId: hija.id, etiqueta, indice: Math.max(indice, 0) };
            cambiar.mutate({
              hija: { id: hija.id, numero: hija.numero },
              madre: null,
              anterior: madre,
            });
          }}
          ocupado={cambiar.isPending}
        />
      ) : (
        <p className="subtareas__vacio">Divide esta tarea en partes de hasta 2 días.</p>
      )}
      {puedeEscribir &&
        (buscando ? (
          <BuscadorTarea
            etiqueta="Buscar una tarea para vincular como subtarea"
            tareaActualId={tarea.id}
            ocupado={cambiar.isPending}
            motivoDeshabilitada={(candidata) => {
              if (candidata.padre?.id === tarea.id) return 'Ya es su subtarea';
              const otra = yaEsSubtareaDe(candidata);
              if (otra) return otra;
              if (candidata.progresoSubtareas) return 'Tiene subtareas propias';
              if (!puedeMoverHija(candidata)) return 'Solo quien la creó puede moverla';
              return null;
            }}
            onElegir={(candidata) =>
              cambiar.mutate(
                { hija: { id: candidata.id, numero: candidata.numero }, madre, anterior: null },
                { onSuccess: cerrarBuscador },
              )
            }
            onCerrar={cerrarBuscador}
          />
        ) : (
          <div className="subtareas__botones">
            {onNuevaSubtarea && (
              <button
                type="button"
                className="boton boton--con-icono"
                onClick={() => onNuevaSubtarea(tarea.id)}
              >
                <Icono nombre="mas" />
                Nueva subtarea
              </button>
            )}
            <button
              ref={vincular}
              type="button"
              className="boton boton--con-icono boton--fantasma"
              onClick={() => setBuscando(true)}
            >
              <Icono nombre="enlace" />
              Vincular existente
            </button>
          </div>
        ))}
    </section>
  );
}
